import { useState, useMemo, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import ImageStage from '@/components/ui/image-stage';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { PHONE_ONLY_QUERY } from '@/config/ui.constants';
import AssetRequestModal from './asset-request-modal';
import OrderServiceModal from '../../property-assets-management/order-service-modal/OrderServiceModal';
import AssetViewToolbar from './asset-view-toolbar';
import PhoneAssetViewer from './phone-asset-viewer';
import ThumbnailSlider from './thumbnail-slider';
import BadgeCarousel from './badge-carousel';
import { useAssetCart } from './use-asset-cart';
import { downloadRemoteFile } from '@/util/downloadFile';
import { ImageAssetType, ServiceType } from './carousel.types';
import type { AssetCartItem } from '@/components/orders/cart/types/cart.types';

// Reserved vertical space (px) for the ThumbnailSlider beneath the image. The stage
// height subtracts this so the modal — image + strip — fits within 90vh. Bump if
// ThumbnailSlider's outer padding or thumb size changes materially.
const THUMB_STRIP_RESERVED_HEIGHT_PX = 110;

const STAGE_HEIGHT = `calc(90vh - ${THUMB_STRIP_RESERVED_HEIGHT_PX}px)`;

/**
 * The image stage is sized from the viewport alone — never from the photo. Both
 * terms matter: the height takes the full budget so the modal doesn't grow
 * taller as the image decodes, and the width is definite so the photo's own
 * dimensions can't feed the dialog's shrink-to-fit sizing (`w-auto` below),
 * which is what made the box jump on load and resize when navigating between
 * landscape and portrait. 80vw floor keeps the established footprint; the
 * ratio term lets a tall viewport widen the stage to fit a landscape photo.
 */
const STAGE_STYLE = {
  height: STAGE_HEIGHT,
  width: `min(95vw, max(80vw, calc(${STAGE_HEIGHT} * 3 / 2)))`
} as const;

interface AssetViewModalProps {
  show: boolean;
  onHide: () => void;
  imageList: ImageAssetType[];
  servicesList?: ServiceType[];
  currentIndex: number;
  propertyAddress: string;
  /** When provided, the toolbar download button routes through this
   * host-owned flow (the Download Assets modal, scoped to the active photo)
   * instead of the legacy direct downloadRemoteFile fallback below. */
  onDownloadRequest?: (photoUUID: string) => void;
}

export default function AssetViewModal({
  show,
  onHide,
  imageList,
  servicesList = [],
  currentIndex,
  propertyAddress,
  onDownloadRequest
}: AssetViewModalProps) {
  const [activeIndex, setActiveIndex] = useState<number>(currentIndex);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [assetRequestModalType, setAssetRequestModalType] = useState<
    'support' | null
  >(null);
  const [hideServicesUI, setHideServicesUI] = useState(false);
  const [assetData, setAssetData] = useState<ImageAssetType[]>([]);
  const [orderModal, setOrderModal] = useState<{
    service: ServiceType;
    photos: ImageAssetType[];
    edit?: {
      lineItemUUID?: string;
      lineAssetId: string;
      initialNote: string;
      originalRefIds: string[];
    };
  } | null>(null);

  const currentImage = imageList[activeIndex];
  const totalImages = imageList?.length ?? 0;

  // max-sm gets the fullscreen swipe viewer (spec §1.11); a screen-size call,
  // not a layout-tier one — tablets and narrow desktop windows at/above sm
  // keep the windowed viewer with its thumbnail strip and services carousel.
  const isPhone = useMediaQuery(PHONE_ONLY_QUERY);

  const { cartAssetIdSet, getCartItemForService, getCartServiceSlugsForAsset } =
    useAssetCart(show);

  useEffect(() => {
    if (show) setActiveIndex(currentIndex);
  }, [show, currentIndex]);

  useEffect(() => {
    setOrderModal(null);
  }, [activeIndex]);

  // When the asset-request modal closes, defer resetting its
  // associated state so the wrapper stays mounted long enough for
  // Radix to play the Dialog's exit animation. 200ms matches the
  // `duration-200` transition baked into DialogContent.
  useEffect(() => {
    if (isHelpModalOpen) return;
    const timer = setTimeout(() => {
      setAssetRequestModalType(null);
    }, 200);
    return () => clearTimeout(timer);
  }, [isHelpModalOpen]);

  useEffect(() => {
    if (!show || isHelpModalOpen || orderModal) return;
    const handler = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          setActiveIndex((i) => Math.max(0, i - 1));
          break;
        case 'ArrowRight':
          e.preventDefault();
          setActiveIndex((i) => Math.min(totalImages - 1, i + 1));
          break;
        case 'Home':
          e.preventDefault();
          setActiveIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setActiveIndex(Math.max(0, totalImages - 1));
          break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [show, isHelpModalOpen, orderModal, totalImages]);

  const images1920 = useMemo(
    () =>
      imageList?.map((it) => it.caches?.['1920w'] || it.caches?.fullSize || ''),
    [imageList]
  );
  const photoName = imageList?.[activeIndex]?.caption || '';

  const currentSrc = images1920?.[activeIndex] || '';
  const [loadedSrcs, setLoadedSrcs] = useState<Set<string>>(new Set());
  const isCurrentLoaded = currentSrc ? loadedSrcs.has(currentSrc) : false;

  const handleImageLoaded = useCallback((url: string) => {
    if (!url) return;
    setLoadedSrcs((prev) => {
      if (prev.has(url)) return prev;
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  }, []);

  // Warm the browser cache for adjacent images so left/right navigation feels instant.
  useEffect(() => {
    if (!show || !images1920) return;
    [activeIndex - 1, activeIndex + 1]
      .filter((i) => i >= 0 && i < images1920.length)
      .forEach((i) => {
        const url = images1920[i];
        if (!url) return;
        const img = new Image();
        img.onload = () => handleImageLoaded(url);
        img.src = url;
      });
  }, [activeIndex, images1920, show, handleImageLoaded]);

  const prevImage = () => setActiveIndex((i) => Math.max(0, i - 1));
  const nextImage = () =>
    setActiveIndex((i) => Math.min(totalImages - 1, i + 1));

  const downloadActive = () => {
    const active = imageList?.[activeIndex];
    if (!active) return;
    if (onDownloadRequest) {
      // Host-owned flow: opens the Download Assets modal scoped to this photo.
      onDownloadRequest(active.photoUUID);
      return;
    }
    // Legacy fallback for hosts that don't supply the modal flow.
    const url = images1920?.[activeIndex];
    if (!url) return;
    // Fetch-then-blob so the download works for cross-origin CDN images that
    // lack a `Content-Disposition: attachment` header (older/legacy assets) —
    // a direct `<a download>` only downloads same-origin URLs. See
    // `downloadRemoteFile`.
    void downloadRemoteFile(url, `${active.caption || 'image'}.jpg`);
  };

  // Resolve a grouped line's photos to full ImageAssetType records from the
  // viewer's imageList (preserving assetRefs order so refs[0] stays the stable
  // first photo). Refs not present in imageList degrade to a minimal record so
  // ordering still has an assetUrl/assetId.
  const resolveLinePhotos = useCallback(
    (item: AssetCartItem): ImageAssetType[] => {
      const refs = item.assetRefs?.length
        ? item.assetRefs
        : [{ assetId: item.assetId, assetUrl: item.assetUrl }];
      return refs.map((ref) => {
        const found = imageList.find((img) => img.assetUUID === ref.assetId);
        if (found) return found;
        return {
          photoUUID: ref.assetId,
          assetUUID: ref.assetId,
          orderUUID: currentImage?.orderUUID ?? '',
          propertyUUID: currentImage?.propertyUUID ?? '',
          photosetUUID: ref.photosetUUID,
          caches: ref.assetUrl
            ? { '1920w': ref.assetUrl, '300w': ref.assetUrl }
            : undefined
        } as ImageAssetType;
      });
    },
    [imageList, currentImage]
  );

  const handleServiceSelect = (serviceId: string) => {
    const service = servicesList.find((s) => s.uuid === serviceId);
    // TODO(DV-2252): staged items are optimistic-only (fake ids) — re-enable
    // once the commit endpoint persists them as real Assets. The badge
    // carousel is hidden for a staged current image, but guard the handler
    // too since it's reachable from tests/future callers.
    if (!service || !currentImage?.assetUUID || currentImage.staged) return;
    const cartItem = getCartItemForService(
      currentImage.assetUUID,
      service.shoppingCartSlug
    );
    if (cartItem) {
      // Already in cart → reopen the unified modal in edit mode with ALL grouped
      // photos and the line's note.
      const refIds = cartItem.assetRefs?.length
        ? cartItem.assetRefs.map((r) => r.assetId)
        : [cartItem.assetId];
      setOrderModal({
        service,
        photos: resolveLinePhotos(cartItem),
        edit: {
          lineItemUUID: cartItem.lineItemUUID,
          lineAssetId: cartItem.assetId,
          initialNote: cartItem.additionalNotes || '',
          originalRefIds: refIds
        }
      });
      return;
    }
    // Fresh pick → create mode for just this photo.
    setOrderModal({ service, photos: [currentImage] });
  };

  const handleOpenHelp = (asset: ImageAssetType) => {
    setAssetRequestModalType('support');
    setAssetData([asset]);
    setIsHelpModalOpen(true);
  };

  const handleMainHide = () => {
    if (!isHelpModalOpen && !orderModal) onHide();
  };

  return (
    <>
      {/* Asset Request Modals */}
      {assetRequestModalType === 'support' && (
        <AssetRequestModal
          show={isHelpModalOpen}
          onHide={() => setIsHelpModalOpen(false)}
          assetData={assetData}
          requestType="support"
          propertyAddress={propertyAddress}
        />
      )}
      {orderModal && (
        <OrderServiceModal
          open
          service={orderModal.service}
          photos={orderModal.photos}
          propertyAddress={propertyAddress}
          edit={orderModal.edit}
          onClose={() => setOrderModal(null)}
        />
      )}

      {/* Main Modal */}
      {/*
        Keep `modal` at its default `true`. Toggling it to `false` when an
        inner asset-request modal opens unmounts this Dialog's overlay (Radix
        skips overlay render when modal=false), then the inner's overlay
        mounts — visible as a flicker on the asset viewer behind it. Stacked
        Radix Dialogs (both modal=true) handle focus + scroll-lock correctly
        via their internal stack: the inner takes focus, the outer stays
        rendered. The preventDefault guards below are still belt-and-suspenders
        for the rare case where an event reaches the outer while the inner is
        open.
      */}
      <Dialog open={show} onOpenChange={(open) => !open && handleMainHide()}>
        <DialogContent
          // Opaque scrim for the phone takeover: the default bg-black/50
          // extends behind Safari's bottom toolbar (fixed = large viewport)
          // while the h-dvh content stops above it, so 50% black over the page
          // read as a gray band below the photo (2026-08-21). Black there is
          // invisible against the viewer's own black surface.
          overlayClassName={isPhone ? 'bg-black' : undefined}
          className={cn(
            'p-0 gap-0 overflow-hidden [&>button]:hidden',
            isPhone
              ? // Fullscreen takeover (spec §1.11): the base DialogContent's
                // centered-window classes are all overridden to pin the
                // content to the viewport edges.
                'inset-0 left-0 top-0 h-dvh max-h-none w-full max-w-none translate-x-0 translate-y-0 rounded-none border-none bg-black'
              : 'w-auto h-auto min-w-[80vw] max-w-[95vw] max-h-[90vh] bg-black border border-white rounded'
          )}
          onPointerDownOutside={(e) => {
            if (isHelpModalOpen || orderModal) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (isHelpModalOpen || orderModal) e.preventDefault();
          }}
        >
          {isPhone ? (
            <PhoneAssetViewer
              photoName={photoName}
              currentIndex={activeIndex}
              totalImages={totalImages}
              src={currentSrc}
              srcSet={`${imageList?.[activeIndex]?.caches?.['830w']} 830w, ${currentSrc} 1920w`}
              placeholderSrc={imageList?.[activeIndex]?.caches?.['300w']}
              loaded={isCurrentLoaded}
              onLoaded={handleImageLoaded}
              isDisabled={isHelpModalOpen || !!orderModal}
              // TODO(DV-2252): staged items are optimistic-only (fake ids) —
              // same rule as the desktop toolbar below.
              showRequestSupport={!currentImage?.staged}
              onRequestSupport={() => {
                const a = imageList?.[activeIndex];
                if (a && !a.staged) handleOpenHelp(a);
              }}
              onDownload={downloadActive}
              onClose={onHide}
              onPrev={prevImage}
              onNext={nextImage}
            />
          ) : (
            <div className="flex flex-col" aria-live="polite">
              <AssetViewToolbar
                photoName={photoName}
                isDisabled={isHelpModalOpen || !!orderModal}
                // TODO(DV-2252): staged items are optimistic-only (fake ids) —
                // re-enable once the commit endpoint persists them as real
                // Assets. An uncommitted staged photo can't be the subject of a
                // support request.
                hideRequestSupport={Boolean(currentImage?.staged)}
                onRequestSupport={() => {
                  const a = imageList?.[activeIndex];
                  if (a && !a.staged) handleOpenHelp(a);
                }}
                onToggleServices={() => setHideServicesUI((prev) => !prev)}
                onDownload={downloadActive}
                onClose={onHide}
              />

              <ImageStage
                src={currentSrc}
                srcSet={`${imageList?.[activeIndex]?.caches?.['830w']} 830w, ${currentSrc} 1920w`}
                sizes="95vw"
                alt={imageList?.[activeIndex]?.caption || `image-${activeIndex}`}
                // The thumbnail strip already painted this derivative, so it
                // fills the stage immediately instead of a spinner on black.
                placeholderSrc={imageList?.[activeIndex]?.caches?.['300w']}
                loaded={isCurrentLoaded}
                onLoaded={handleImageLoaded}
                className="bg-black"
                style={STAGE_STYLE}
                spinnerClassName="text-white/85"
              >
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 top-0 h-26 z-[28]"
                  // Multi-stop ease-out curve: each segment's slope is ~70% of the
                  // previous one, so the bottom asymptotes toward transparent and
                  // the trailing edge is imperceptible against the photo. Defined
                  // as --gradient-image-fade in app.css.
                  style={{ background: 'var(--gradient-image-fade)' }}
                />
                <NavOverlayButton
                  direction="left"
                  disabled={activeIndex === 0}
                  onClick={prevImage}
                />
                <NavOverlayButton
                  direction="right"
                  disabled={activeIndex === totalImages - 1}
                  onClick={nextImage}
                />
                {/* TODO(DV-2252): staged items are optimistic-only (fake ids) —
                    re-enable once the commit endpoint persists them as real
                    Assets. An uncommitted staged photo can't be ordered against. */}
                {!hideServicesUI && !currentImage?.staged && (
                  <BadgeCarousel
                    services={servicesList}
                    selectedServiceId={null}
                    onSelect={handleServiceSelect}
                    cartServiceSlugs={getCartServiceSlugsForAsset(
                      currentImage?.assetUUID ?? ''
                    )}
                  />
                )}
              </ImageStage>

              <ThumbnailSlider
                images={imageList || []}
                activeIndex={activeIndex}
                onSelect={setActiveIndex}
                cartAssetIds={cartAssetIdSet}
                focusOnMount
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function NavOverlayButton({
  direction,
  disabled,
  onClick
}: {
  direction: 'left' | 'right';
  disabled: boolean;
  onClick: () => void;
}) {
  const Icon = direction === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      aria-label={`${direction === 'left' ? 'previous' : 'next'} image`}
      onClick={onClick}
      className={cn(
        'absolute top-1/2 -translate-y-1/2 z-40',
        'bg-white/85 rounded-3xl w-8 h-[88px]',
        'flex items-center justify-center border-none cursor-pointer',
        'transition-all duration-200',
        'disabled:cursor-default disabled:opacity-50',
        direction === 'left' ? 'left-3' : 'right-3'
      )}
      disabled={disabled}
    >
      <Icon className="h-6 w-6" />
    </button>
  );
}
